/**
 * Shared universal-search engine — one cache + one activate path used by BOTH
 * the inline Dashboard search bar (autocomplete dropdown) and the Quick Search
 * modal (Ctrl+K / title bar). Keeps them behaviourally identical.
 *
 * Backed by GET /api/search-cache (users, groups, shared mailboxes, file
 * servers, printers). The cache is fetched once and reused ~5 min; typing
 * filters it client-side. Activating a result opens that entity's own modal
 * where one exists, else navigates to its tab pre-filtered.
 */

import { fetchSearchCache } from '@/api/endpoints/search.js';
import { useUiStore } from '@/state/uiStore.js';
import { useUserMgmtStore } from '@/tabs/UserManagement/state.js';
import { useGroupMgmtStore } from '@/tabs/GroupManagement/state.js';
import { useSharedMailboxStore } from '@/tabs/SharedMailboxes/state.js';
import { useSharesStore } from '@/tabs/Shares/state.js';
import { usePrintersStore } from '@/tabs/Printers/state.js';
import { useAuthStore } from '@/state/authStore.js';

const CACHE_TTL_MS = 5 * 60 * 1000;

export const GROUP_META = {
  user: { label: 'Users', icon: 'fa-user', gradient: 'linear-gradient(135deg,#667eea,#764ba2)' },
  group: { label: 'Groups', icon: 'fa-users', gradient: 'linear-gradient(135deg,#8b5cf6,#7c3aed)' },
  mailbox: { label: 'Shared Mailboxes', icon: 'fa-envelope', gradient: 'linear-gradient(135deg,#0ea5e9,#0369a1)' },
  device: { label: 'Devices', icon: 'fa-laptop', gradient: 'linear-gradient(135deg,#10b981,#059669)' },
  server: { label: 'File Servers', icon: 'fa-server', gradient: 'linear-gradient(135deg,#64748b,#475569)' },
  share: { label: 'File Shares', icon: 'fa-folder-open', gradient: 'linear-gradient(135deg,#0891b2,#0e7490)' },
  drive: { label: 'Mapped Drives', icon: 'fa-hdd', gradient: 'linear-gradient(135deg,#6366f1,#4338ca)' },
  printer: { label: 'Printers', icon: 'fa-print', gradient: 'linear-gradient(135deg,#f59e0b,#b45309)' }
};

export const KIND_ORDER = ['user', 'group', 'device', 'mailbox', 'server', 'share', 'drive', 'printer'];

/** Module-level cache so both surfaces share one fetch. */
let cachedItems = null;
let cachedAt = 0;
let inFlight = null;

function str(v) {
  return typeof v === 'string' ? v : v == null ? '' : String(v);
}

/** Flatten the raw cache buckets into searchable items (keep raw `record`). */
function normalise(buckets) {
  const items = [];
  for (const u of buckets?.users?.data ?? []) {
    const name = str(u.displayName) || str(u.upn) || str(u.mail);
    const sub = [str(u.mail) || str(u.upn), str(u.jobTitle), str(u.department)].filter(Boolean).join(' · ');
    // Match the User Management search's field set so universal search finds the
    // same users: name, UPN, mail, given/surname, title, dept, company, office,
    // phones and proxy addresses (aliases) — not just the two fields shown.
    const hay = [
      name, str(u.upn), str(u.mail), str(u.givenName), str(u.surname),
      str(u.jobTitle), str(u.department), str(u.companyName), str(u.officeLocation),
      str(u.mobilePhone), (Array.isArray(u.businessPhones) ? u.businessPhones.join(' ') : ''),
      (Array.isArray(u.proxyAddresses) ? u.proxyAddresses.join(' ') : '')
    ].join(' ').toLowerCase();
    items.push({ kind: 'user', name, sub, record: u, hay });
  }
  for (const g of buckets?.groups?.data ?? []) {
    const name = str(g.displayName) || str(g.name);
    const sub = [str(g.sourceLabel) || str(g.source), str(g.mail)].filter(Boolean).join(' · ');
    items.push({ kind: 'group', name, sub, record: g, hay: `${name} ${sub} ${str(g.mail)}`.toLowerCase() });
  }
  for (const m of buckets?.sharedMailboxes?.data ?? []) {
    const name = str(m.displayName) || str(m.name);
    const email = str(m.email) || str(m.primarySmtpAddress) || (Array.isArray(m.emailAddresses) ? str(m.emailAddresses[0]) : '');
    items.push({ kind: 'mailbox', name, sub: email, record: m, hay: `${name} ${email}`.toLowerCase() });
  }
  for (const s of buckets?.fileServers?.data ?? []) {
    const name = str(s.name) || str(s.dnsHostName);
    const sub = [str(s.description), typeof s.shareCount === 'number' ? `${s.shareCount} shares` : ''].filter(Boolean).join(' · ');
    items.push({ kind: 'server', name, sub, record: s, hay: `${name} ${str(s.dnsHostName)} ${str(s.ipAddress)} ${str(s.description)}`.toLowerCase() });
  }
  for (const p of buckets?.printers?.data ?? []) {
    const name = str(p.name) || str(p.displayName);
    const sub = [str(p.site) || str(p.office), str(p.make)].filter(Boolean).join(' · ');
    items.push({ kind: 'printer', name, sub, record: p, hay: `${name} ${str(p.site)} ${str(p.make)} ${str(p.serialNumber)} ${str(p.ipAddress)}`.toLowerCase() });
  }
  for (const sh of buckets?.shares?.data ?? []) {
    const name = str(sh.name);
    const sub = [str(sh.server), str(sh.description)].filter(Boolean).join(' · ');
    items.push({ kind: 'share', name, sub, record: sh, hay: `${name} ${str(sh.server)} ${str(sh.path)} ${str(sh.description)}`.toLowerCase() });
  }
  for (const dr of buckets?.drives?.data ?? []) {
    const letter = str(dr.driveLetter);
    const name = [letter && `${letter.replace(/:$/, '')}:`, str(dr.label) || str(dr.path)].filter(Boolean).join(' ');
    const sub = [str(dr.path), str(dr.description)].filter(Boolean).join(' · ');
    items.push({
      kind: 'drive', name, sub, record: dr,
      hay: `${str(dr.label)} ${str(dr.path)} ${letter} ${str(dr.description)} ${(dr.securityGroups || []).join(' ')}`.toLowerCase()
    });
  }
  for (const d of buckets?.devices?.data ?? []) {
    const name = str(d.name);
    const owner = str(d.primaryUserName) || str(d.primaryUser);
    const sub = [str(d.os), owner].filter(Boolean).join(' · ');
    // agentState drives the online indicator + whether "Remote Tools" is the action.
    const agentState = d.hasAgent ? (d.agentOnline ? 'online' : 'offline') : 'none';
    // Include BOTH the owner's display name and UPN so typing a person's name
    // (or email) surfaces their devices.
    items.push({
      kind: 'device', name, sub, record: d, agentState,
      hay: `${name} ${str(d.os)} ${str(d.primaryUserName)} ${str(d.primaryUser)} ${str(d.serialNumber)}`.toLowerCase()
    });
  }
  return items;
}

/**
 * Load the searchable item list (cached ~5 min, single-flight). Throws on a
 * fetch error so callers can show an error state.
 * @returns {Promise<any[]>}
 */
export async function loadSearchItems() {
  if (cachedItems && Date.now() - cachedAt < CACHE_TTL_MS) return cachedItems;
  if (inFlight) return inFlight;
  inFlight = fetchSearchCache()
    .then((res) => {
      cachedItems = normalise(res?.cache ?? {});
      cachedAt = Date.now();
      return cachedItems;
    })
    .finally(() => { inFlight = null; });
  return inFlight;
}

/** Group + limit matches for a query. Empty query → no groups. */
export function buildResultGroups(items, query, perGroupLimit = 6) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  // Token-based AND match (like the User Management search): every whitespace-
  // separated term must appear somewhere in the haystack, so "test user" or
  // "martin ka" match regardless of word order or the gap between them — not
  // only as one contiguous substring.
  const terms = q.split(/\s+/).filter(Boolean);
  const matched = items.filter((it) => terms.every((t) => it.hay.includes(t)));
  const byKind = {};
  for (const it of matched) (byKind[it.kind] ??= []).push(it);
  return KIND_ORDER
    .filter((k) => byKind[k]?.length)
    .map((k) => ({ kind: k, meta: GROUP_META[k], all: byKind[k], shown: byKind[k].slice(0, perGroupLimit) }));
}

/**
 * Open the picked entity's own modal where one exists; else navigate to the
 * owning tab pre-filtered. Stores are global, so setting open-state before/
 * after the tab switch makes the tab render the modal as it mounts.
 */
export function activateSearchItem(item) {
  if (!item) return;
  const setActiveTab = useUiStore.getState().setActiveTab;
  const r = item.record ?? {};
  switch (item.kind) {
    case 'user': {
      setActiveTab('user-management-v2');
      const dn = str(r.dn) || str(r.onPremisesDn) || str(r.distinguishedName) || str(r.upn);
      useUserMgmtStore.getState().openUserDetail?.(dn);
      break;
    }
    case 'group':
      setActiveTab('group-management');
      useGroupMgmtStore.getState().openMembers?.(r, false);
      break;
    case 'mailbox':
      setActiveTab('shared-mailboxes');
      useSharedMailboxStore.getState().openDetails?.(r);
      break;
    case 'device': {
      // If an agent is enrolled and the operator has the permission, open the
      // Remote Access Tools modal directly; otherwise land on Asset Management.
      const host = str(r.agentHostname) || str(r.name).split('.')[0].toLowerCase();
      const canRemote = useAuthStore.getState().hasPermission?.('remote-tools');
      if (r.hasAgent && host && canRemote) {
        useUiStore.getState().openRemoteTools?.({ hostname: host, deviceName: str(r.name) });
      } else {
        setActiveTab('asset-management');
      }
      break;
    }
    case 'server':
      setActiveTab('shares');
      useSharesStore.getState().setServerSearchQuery?.(str(r.name) || item.name);
      break;
    case 'share':
      // Land on the Shares tab filtered to the server that hosts the share.
      setActiveTab('shares');
      useSharesStore.getState().setServerSearchQuery?.(str(r.server));
      break;
    case 'drive':
      // Mapped-drive config lives in Admin → Drive Map.
      useUiStore.getState().openAdminSettings?.('drive-map');
      break;
    case 'printer':
      setActiveTab('printer-management');
      usePrintersStore.getState().setSearchQuery?.(str(r.name) || item.name);
      break;
    default:
      break;
  }
}
